import {
  PlatformController,
  MetaData,
  ObsidianActionHooks,
  PlatformState,
} from '../types';
import {Platform} from '../../../types';

const MAX_PLAYER_STATE_RESPONSE_TIME = 10000;

export default class SpotifyController implements PlatformController {
  private spotifyPlayer?: Spotify.SpotifyPlayer;
  private authToken?: string;
  private deviceId?: string;
  private initialized: boolean;
  private currentMetaData?: MetaData;
  private actions: ObsidianActionHooks;

  constructor(actions: ObsidianActionHooks) {
    this.initialized = false;
    this.actions = actions;
  }

  play(id: string, callback: () => void) {
    try {
      fetch(
        `https://api.spotify.com/v1/me/player/play?device_id=${this.deviceId}`,
        {
          method: 'PUT',
          body: JSON.stringify({uris: [`spotify:track:${id}`]}),
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.authToken}`,
          },
        },
      ).then(() => {
        // Spotify Web Playback SDK doesn't support playing tracks via URI, instead we use Web API.
        // Since Web API doesn't provide a promise that resolves when the song starts playing,
        // we attach a playback_state_changed listener that triggers a callback if the updated state
        // confirms the corresponding song has begun playing. Then the listener is detached.
        const callbackAndDetach = (state: Spotify.PlaybackState) => {
          // TODO: Spotify occasionally moves ids, so you can play an ID, but the next state
          // claims to be playing a different id if they both map to same song. eg.
          // 3O8X1DE9btbzy4UH9cSX9a and 5hM5arv9KDbCHS0k9uqwjr both map to Tame Impala Borderline.
          // In this case, our elapsed time may be incorrect, but will correct itself after next state update.
          // Need better method of tying next state update to corresponding play request. It's possible this id
          // match check can be removed if race condition not possible (no state updates in-between requests)
          if (state.track_window.current_track.id === id) {
            this.actions.onNewSong();
            callback();
            this.spotifyPlayer &&
              this.spotifyPlayer.removeListener(
                'player_state_changed',
                callbackAndDetach,
              );
            clearTimeout(waitForPlayerStateTime);
          }
        };

        this.spotifyPlayer &&
          this.spotifyPlayer.addListener(
            'player_state_changed',
            callbackAndDetach,
          );

        // In case we've waited a while and haven't received the corresponding playback_state_changed
        // cancel the listener.
        const waitForPlayerStateTime = setTimeout(() => {
          this.spotifyPlayer &&
            this.spotifyPlayer.removeListener(
              'player_state_changed',
              callbackAndDetach,
            );
        }, MAX_PLAYER_STATE_RESPONSE_TIME);
      });
    } catch (error) {
      console.log('spotify error while attempting to play track:');
      console.log(error);
    }
  }

  async stop() {}

  pause() {
    this.spotifyPlayer && this.spotifyPlayer.pause();
  }

  resume() {
    this.spotifyPlayer && this.spotifyPlayer.resume();
  }

  togglePlay() {
    this.spotifyPlayer && this.spotifyPlayer.togglePlay();
  }

  seek(position: number) {
    this.spotifyPlayer && this.spotifyPlayer.seek(position);
  }

  nextTrack() {
    this.spotifyPlayer && this.spotifyPlayer.togglePlay();
    this.spotifyPlayer && this.spotifyPlayer.nextTrack();
  }

  previousTrack() {
    this.spotifyPlayer && this.spotifyPlayer.previousTrack();
  }

  setVolume(value: number) {
    this.spotifyPlayer && this.spotifyPlayer.setVolume(value);
  }

  isReady() {
    return this.initialized;
  }

  async initialize(secret: string) {
    this.authToken = secret;

    this.spotifyPlayer = new window.Spotify.Player({
      name: 'Alto',
      getOAuthToken: (cb: any) => {
        cb(secret);
      },
    });

    this.spotifyPlayer.addListener(
      'initialization_error',
      ({message}: {message: string}) => {
        console.error(message);
        this.initialized = false;
      },
    );

    this.spotifyPlayer.addListener(
      'authentication_error',
      ({message}: {message: string}) => {
        console.error(message);
        this.initialized = false;
      },
    );

    this.spotifyPlayer.addListener(
      'account_error',
      ({message}: {message: string}) => {
        console.error(message);
        this.initialized = false;
      },
    );

    // TODO: if message contains playback id, remove any occurences in queue / currentsong
    this.spotifyPlayer.addListener(
      'playback_error',
      ({message}: {message: string}) => {
        console.error(message);
        this.initialized = false;
      },
    );

    this.spotifyPlayer.addListener(
      'player_state_changed',
      (state: Spotify.PlaybackState) => {
        if (!state) {
          this.actions.onBadPlayerState('Spotify returned null state');
          return;
        }
        const {paused} = state;
        if (!paused) {
          const nextMetaData = this.getTrackMetaData(state);
          if (this.currentMetaData !== nextMetaData && nextMetaData) {
            this.currentMetaData = nextMetaData;
            this.actions.onMetaDataChange(nextMetaData);
            this.actions.onPlay();
          }
        }
        paused && this.actions.onPause();
      },
    );

    this.spotifyPlayer.addListener('ready', ({device_id}: any) => {
      console.log('Ready with Device ID', device_id);
      this.deviceId = device_id;
      this.initialized = true;
      this.actions.onConnected(Platform.Spotify);
    });

    this.spotifyPlayer.addListener('not_ready', ({device_id}: any) => {
      console.log('Spotify device ID has gone offline', device_id);
      this.initialized = false;
    });

    this.spotifyPlayer.connect();

    return this.initialized;
  }

  getTrackMetaData(state: any): MetaData | null {
    const {track_window, position, duration} = state;
    if (!track_window || !track_window.current_track) {
      return null;
    }
    const {album, artists, name} = track_window.current_track;
    const albumImage =
      album && album.images && album.images[1] && album.images[1].url;
    const artist = artists && artists[0] && artists[0].name;
    return {
      name,
      position,
      duration,
      artist,
      albumImage,
    };
  }

  getState(): PlatformState {
    return {
      platform: Platform.Spotify,
      linked: true, // TODO: Use user settings to determine if user has Linked Spotify in the past.
      connected: Boolean(this.spotifyPlayer),
    };
  }
}
